import React, { useState, useEffect } from 'react';
import { Box, Typography, Tab, Tabs } from '@mui/material';
import './Revenue.css';
import { axiosInstance } from '../../../utils/tokenUtils';
import dayjs from 'dayjs';

const formatCurrency = value => {
  if (Number.isNaN(value) || value === null || value === undefined) return '$ 0';
  const formatter = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 });
  return `${formatter.format(value)} Koins`;
};

const getSubtitleForPeriod = periodKey => {
  switch (periodKey) {
    case '1_year':
      return 'Average of year';
    case '6_months':
      return 'Average of 6 months';
    case '3_months':
      return 'Average of 3 months';
    case '1_month':
      return 'Average of month';
    default:
      return 'Average of month';
  }
};

const Revenue = () => {
  const [value, setValue] = useState(0);
  const [periodKey, setPeriodKey] = useState('1_year'); // one of: 1_month, 3_months, 6_months, 1_year
  const [revenueData, setRevenueData] = useState({
    monthly: 0,
    percentage: 0,
    total: 0,
    chartData: [], // { label: string, amount: number }[] based on API
  });

  const handleChange = (event, newValue) => {
    setValue(newValue);

    // Map selected tab to API period
    if (newValue === 0) {
      setPeriodKey('1_year');
    } else if (newValue === 1) {
      setPeriodKey('6_months');
    } else if (newValue === 2) {
      setPeriodKey('3_months');
    } else {
      setPeriodKey('1_month');
    }
  };

  useEffect(() => {
    let isCancelled = false;

    const fetchRevenueData = async () => {
      try {
        const response = await axiosInstance.get('/teachers/revenue-growth-chart', {
          params: { period: periodKey },
        });

        const apiData = response?.data || {};
        const dataPoints = Array.isArray(apiData.data_points) ? apiData.data_points : [];
        const periodType = apiData.period_type || '';

        const chartData = dataPoints.map(point => {
          const amount = typeof point.revenue === 'number' ? point.revenue : 0;
          let label = '';
          if (periodType === 'monthly' && point.period) {
            label = String(point.period);
          } else if (point.date) {
            label = dayjs(point.date).isValid()
              ? dayjs(point.date).format('DD MMM')
              : String(point.period || '');
          } else {
            label = String(point.period || '');
          }
          return { label, amount };
        });

        // Handle growth rate possibly being in fraction or percentage
        let growthRate =
          typeof apiData.revenue_growth_rate === 'number' ? apiData.revenue_growth_rate : 0;
        if (Math.abs(growthRate) <= 1) {
          growthRate = growthRate * 100;
        }

        const avgMonthly =
          typeof apiData.average_revenue_per_month === 'number'
            ? apiData.average_revenue_per_month
            : 0;
        const totalRevenue = typeof apiData.total_revenue === 'number' ? apiData.total_revenue : 0;

        if (!isCancelled) {
          setRevenueData({
            monthly: avgMonthly,
            percentage: Number(growthRate.toFixed(2)),
            total: totalRevenue,
            chartData,
          });
        }
      } catch (error) {
        console.error('Error fetching revenue data:', error);
        if (!isCancelled) {
          setRevenueData(prev => ({ ...prev, chartData: [] }));
        }
      }
    };

    fetchRevenueData();

    return () => {
      isCancelled = true;
    };
  }, [periodKey]);

  // Find the maximum value for scaling (guard against empty data)
  const maxAmount = revenueData.chartData.length
    ? Math.max(...revenueData.chartData.map(item => item.amount))
    : 0;

  // Highlight the latest period from the data, if available
  const highlightedLabel = revenueData.chartData.length
    ? revenueData.chartData[revenueData.chartData.length - 1].label
    : '';

  return (
    <Box className="revenue-card">
      <Box className="revenue-header">
        <Box>
          <Typography variant="h6" className="revenue-title">
            Revenue
          </Typography>
          <Typography variant="body2" className="revenue-subtitle">
            {getSubtitleForPeriod(periodKey)}
          </Typography>
          <Box className="revenue-amount-container">
            <Typography variant="h4" className="revenue-amount">
              {formatCurrency(revenueData.monthly)}
            </Typography>
            <Typography
              variant="body2"
              className={`revenue-percentage ${revenueData.percentage >= 0 ? 'positive' : 'negative'}`}
            >
              {revenueData.percentage >= 0 ? '↑' : '↓'} {Math.abs(revenueData.percentage)}%
            </Typography>
          </Box>
          <Typography variant="body2" className="revenue-total">
            Total revenue: {formatCurrency(revenueData.total)}
          </Typography>
        </Box>
        <Box>
          <Tabs value={value} onChange={handleChange} className="revenue-tabs">
            <Tab label="1 Year" className="revenue-tab" />
            <Tab label="6 months" className="revenue-tab" />
            <Tab label="3 months" className="revenue-tab" />
            <Tab label="1 month" className="revenue-tab" />
          </Tabs>
        </Box>
      </Box>

      <Box className="revenue-chart-container">
        {/* Y-axis label */}
        {/* <Typography variant="body2" className="revenue-chart-y-label">
          {formatCurrency(maxAmount)}
        </Typography> */}

        <Box className="revenue-chart">
          {revenueData.chartData.map((item, index) => {
            const percent = maxAmount ? (item.amount / maxAmount) * 100 : 0;
            const showValue = item.amount > 0;
            return (
              <Box
                key={`${item.label}-${index}`}
                className={`revenue-bar-container ${item.label === highlightedLabel ? 'highlighted' : ''}`}
              >
                {showValue && (
                  <Typography
                    variant="caption"
                    className="revenue-bar-value"
                    sx={{ bottom: `min(calc(100% - 18px), max(28px, calc(${percent}% + 8px)))` }}
                  >
                    {formatCurrency(item.amount)}
                  </Typography>
                )}
                <Box
                  className="revenue-bar"
                  sx={{
                    height: `${percent}%`,
                    backgroundColor: item.label === highlightedLabel ? '#1A73E8' : '#E8F0FB',
                  }}
                />
                <Typography variant="caption" className="revenue-bar-label">
                  {item.label}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default Revenue;
